import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, ArrowRight, Brain } from 'lucide-react';

export default function ProactiveInsight({ insight, onDismiss, onAction }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (insight) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [insight]);

  if (!insight) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="relative overflow-hidden bg-gradient-to-r from-purple-600/20 via-pink-500/15 to-amber-500/10 backdrop-blur-xl rounded-2xl border border-purple-400/30 p-5"
          data-testid="proactive-insight"
        >
          {/* Shimmer effect */}
          <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 4, ease: 'linear' }} />

          <div className="relative">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <motion.div
                  className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}>
                  <Brain className="w-4 h-4 text-white" strokeWidth={1.5} />
                </motion.div>
                <div>
                  <span className="text-purple-300 text-[10px] font-inter font-bold uppercase tracking-[0.2em]">AI Insight</span>
                  <div className="flex items-center space-x-1">
                    <Sparkles className="w-2.5 h-2.5 text-amber-400" />
                    <span className="text-amber-400/80 text-[9px] font-inter">Personalized for you</span>
                  </div>
                </div>
              </div>
              <button onClick={() => { setVisible(false); onDismiss?.(); }}
                className="text-white/30 hover:text-white/60 transition-colors" data-testid="dismiss-insight">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-white/90 font-inter text-sm leading-relaxed mb-4">{insight.message}</p>

            {insight.action && (
              <motion.button
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={() => onAction?.(insight.action_path)}
                className="flex items-center space-x-2 bg-white/10 hover:bg-white/15 border border-white/10 rounded-full px-4 py-2 text-white/80 text-xs font-inter font-medium transition-all"
                data-testid="insight-action-btn">
                <span>{insight.action}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </motion.button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
